import React from 'react';
import { Card, Space, Table, Typography } from 'antd';
import { TAB_PREFIXES, TIER_LABELS, freezeCapacity } from './dailyEngagementConfig';
import { useDraft } from './draftContext';
import { ErrorList, LabeledNumber } from './fields';

const { Paragraph, Text } = Typography;

/** Streak freezes: storage cap, Match Token price and the free freezes. */
export const FreezesTab: React.FC = () => {
  const { draft, update } = useDraft();
  const f = draft.freezes;
  const capacityRows = [
    { key: 'none', label: 'No tier yet', index: 0 },
    ...draft.loyalty.tiers.map((t, i) => ({
      key: `${t.tier}-${i}`,
      label: TIER_LABELS[t.tier] ?? t.tier,
      index: i + 1,
    })),
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <ErrorList prefixes={TAB_PREFIXES.freezes} />

      <Card size="small" title="Streak freezes">
        <Paragraph type="secondary">
          A missed IST day uses one freeze automatically; the protected day keeps the streak and
          stays claimable. Misses beyond the freezes held restart the cycle at Day 1. A loyalty
          vault always grants all its freezes: what fits under the storage cap is regular stock, the
          rest is overflow, used first on a missed day and moved into regular stock when room
          appears. Buying is blocked while regular + overflow is at or above the cap.
        </Paragraph>
        <Space wrap size="large" align="start">
          <LabeledNumber
            label="Storage cap (before loyalty)"
            path="freezes.baseCapacity"
            value={f.baseCapacity}
            min={0}
            help="Whole number, 0 or more. Loyalty tiers add to it (Loyalty tab)."
            onChange={(v) =>
              update((d) => {
                d.freezes.baseCapacity = v;
              })
            }
          />
          <LabeledNumber
            label="Price of one freeze"
            path="freezes.purchaseCostMatchTokens"
            value={f.purchaseCostMatchTokens}
            min={1}
            addonAfter="Match Tokens"
            help="Whole number, at least 1."
            onChange={(v) =>
              update((d) => {
                d.freezes.purchaseCostMatchTokens = v;
              })
            }
          />
          <LabeledNumber
            label="Free freezes"
            path="freezes.freeFreezes"
            value={f.freeFreezes}
            min={0}
            help="Granted once, when an account first opens Daily Rewards. Changing it does not reach accounts that already have their state."
            onChange={(v) =>
              update((d) => {
                d.freezes.freeFreezes = v;
              })
            }
          />
        </Space>
      </Card>

      <Card size="small" title="Storage cap by loyalty tier (read-only)">
        <Paragraph type="secondary">
          Storage cap + every reached tier&apos;s freeze capacity bonus. Tier thresholds and bonuses
          are on the Loyalty tab.
        </Paragraph>
        <Table
          size="small"
          pagination={false}
          rowKey="key"
          dataSource={capacityRows}
          columns={[
            { title: 'Tier', dataIndex: 'label' },
            {
              title: 'Freezes a player can hold',
              render: (_v, row) => {
                const cap = freezeCapacity(draft, row.index);
                return Number.isFinite(cap) ? <Text strong>{cap}</Text> : '?';
              },
            },
          ]}
        />
      </Card>
    </Space>
  );
};
